import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Stack, TextField, Alert, Paper,
  Tabs, Tab, Grid, Chip, CircularProgress, IconButton, Snackbar, Dialog,
  DialogTitle, DialogContent, DialogActions, FormLabel, FormControlLabel, Checkbox,
  Divider, Tooltip, Avatar, Switch, Badge
} from '@mui/material';
import {
  CloudUpload, Facebook, Instagram, Delete, Launch, Refresh, CheckCircle,
  Schedule, ErrorOutline, Share, SmartDisplay, PhotoLibrary, PostAdd, Link as LinkIcon,
  Campaign, Analytics, Movie, AddPhotoAlternate, Tag, EmojiEmotions,
  ThumbUp, ChatBubbleOutline, BarChart, OpenInNew, Lock
} from '@mui/icons-material';

import { socialMediaAPI, metaAPI, metaAdsAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import FacebookConnectButton from '../components/FacebookConnectButton';
import MetaAdsManager from '../components/MetaAdsManager';


export default function SocialOperations() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageInfoLoading, setPageInfoLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Post Creation Form State
  const [caption, setCaption] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaPreview, setMediaPreview] = useState('');
  const [mediaType, setMediaType] = useState('image'); // 'image' | 'video'
  const [targetPlatforms, setTargetPlatforms] = useState(['facebook', 'instagram']);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [previewTab, setPreviewTab] = useState('facebook'); // 'facebook' | 'instagram'

  // Connected Meta Account State
  const [metaAccount, setMetaAccount] = useState(null);
  const [metaAccountLoading, setMetaAccountLoading] = useState(true);

  // Delete Confirm Dialog
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadMetaAccount();
    loadPageInfo();
    loadPosts();

    // Auto-refresh posts & analytics metrics every 15 seconds
    const interval = setInterval(() => {
      loadPosts();
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const loadMetaAccount = async () => {
    setMetaAccountLoading(true);
    try {
      const res = await metaAdsAPI.getConnectedAccount();
      if (res.data.success && res.data.data) {
        setMetaAccount(res.data.data);
      } else {
        setMetaAccount(null);
      }
    } catch (err) {
      setMetaAccount(null);
    } finally {
      setMetaAccountLoading(false);
    }
  };


  const loadPageInfo = async () => {
    setPageInfoLoading(true);
    try {
      const res = await socialMediaAPI.getPageInfo();
      if (res.data.success) {
        setPageInfo(res.data);
      }
    } catch (err) {
      console.error('Failed to load Page info:', err);
    } finally {
      setPageInfoLoading(false);
    }
  };

  const [syncingPostId, setSyncingPostId] = useState(null);

  const loadPosts = async () => {
    setPostsLoading(true);
    try {
      const res = await socialMediaAPI.getPosts();
      if (res.data.success) {
        setPosts(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load social posts:', err);
    } finally {
      setPostsLoading(false);
    }
  };

  const handleSyncSinglePost = async (postId) => {
    setSyncingPostId(postId);
    try {
      const res = await socialMediaAPI.syncPostMetrics(postId);
      if (res.data.success && res.data.data) {
        setPosts((prevPosts) =>
          prevPosts.map((p) => (p._id === postId ? { ...p, ...res.data.data } : p))
        );
        setSnackbar({ open: true, message: 'Live post likes & comments updated!', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to sync post metrics',
        severity: 'error',
      });
    } finally {
      setSyncingPostId(null);
    }
  };


  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setMediaFile(file);
    const isVideo = file.type.startsWith('video/');
    setMediaType(isVideo ? 'video' : 'image');

    // Create local object URL for instant preview
    const localUrl = URL.createObjectURL(file);
    setMediaPreview(localUrl);

    // Upload to server to get public URL
    setUploadingMedia(true);
    try {
      const res = await socialMediaAPI.uploadMedia(file);
      const uploadedUrl = res.data?.data?.url || res.data?.url;
      if (uploadedUrl) {
        setMediaUrl(uploadedUrl);
        setSnackbar({ open: true, message: 'Media uploaded successfully!', severity: 'success' });
      } else {
        setMediaUrl(localUrl);
      }
    } catch (err) {
      setMediaUrl(localUrl);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Using local preview for media',
        severity: 'info',
      });
    } finally {
      setUploadingMedia(false);
    }
  };

  const handlePlatformToggle = (platform) => {
    setTargetPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const handleAddHashtag = (tag) => {
    setCaption((prev) => (prev ? `${prev} ${tag}` : tag));
  };

  const handleAddEmoji = (emoji) => {
    setCaption((prev) => `${prev}${emoji}`);
  };

  const handlePublish = async () => {
    const finalMediaUrl = mediaUrl || mediaPreview;
    if (!finalMediaUrl) {
      setSnackbar({ open: true, message: 'Please upload an image/video or enter a media URL first', severity: 'warning' });
      return;
    }
    if (targetPlatforms.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one target platform', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const rawPageId = localStorage.getItem('selectedMetaPageId');
      const selectedPageId = rawPageId && /^\d+$/.test(rawPageId.trim()) ? rawPageId.trim() : undefined;
      const payload = {
        pageId: selectedPageId,

        mediaUrl: finalMediaUrl,
        mediaType,
        caption,
        targetPlatforms,
        scheduledAt: isScheduled ? scheduledAt : null,
      };



      const res = await socialMediaAPI.publishPost(payload);
      if (res.data.success) {
        setSnackbar({ open: true, message: res.data.message, severity: 'success' });
        // Reset form
        setCaption('');
        setMediaFile(null);
        setMediaUrl('');
        setMediaPreview('');
        setIsScheduled(false);
        setScheduledAt('');
        loadPosts();
        setTab(1); // Switch to Posts list tab
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Social media publishing failed',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async () => {
    if (!deleteId) return;
    try {
      await socialMediaAPI.deletePost(deleteId);
      setSnackbar({ open: true, message: 'Post record deleted', severity: 'info' });
      setDeleteId(null);
      loadPosts();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete post record', severity: 'error' });
    }
  };

  const popularHashtags = ['#FitisticGym', '#FitnessGoal', '#GymMotivation', '#WorkoutDaily', '#HealthyLifestyle', '#PersonalTraining'];
  const popularEmojis = ['🏋️‍♂️', '🔥', '💪', '⚡', '🎯', '🌟', '🥗', '🏆'];

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Social Media Operations"
        subtitle="Publish images, videos & reels directly to your business Facebook Page and Instagram Account."
        gradient="linear-gradient(90deg, #1D4ED8, #7C3AED)"
        actions={<Button variant="outlined" size="small" startIcon={<Refresh />} onClick={() => { loadPageInfo(); loadPosts(); }}>Refresh</Button>}
      />

      {/* Top Section: Meta Account & Page Connection Header */}
      <MetaAdsManager showHeaderOnly={true} />

      {/* Lower Operations Container (Locked with blur overlay if no account connected) */}
      <Box sx={{ position: 'relative', minHeight: 420, mt: 3 }}>
        {/* Navigation Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Create & Publish" />
            <Tab label={`Posts History (${posts.filter((p) => p.status !== 'failed').length})`} />
            <Tab label="Meta Campaigns & Lead Forms" />
          </Tabs>
        </Paper>




      {/* ── TAB 0: CREATE & PUBLISH ────────────────────────────────────────── */}
      {tab === 0 && (
        <Grid container spacing={3}>
          {/* Left Column: Post Composer */}
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Create New Social Post
                </Typography>

                {/* Media Uploader Area */}
                <Box mb={3}>
                  <FormLabel sx={{ fontWeight: 600, mb: 1, display: 'block' }}>Media Asset (Image or Video / Reel)</FormLabel>

                  {mediaPreview ? (
                    <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'black', textAlign: 'center' }}>
                      {mediaType === 'video' ? (
                        <video src={mediaPreview} controls style={{ maxHeight: 320, width: '100%', objectFit: 'contain' }} />
                      ) : (
                        <img src={mediaPreview} alt="Preview" style={{ maxHeight: 320, width: '100%', objectFit: 'contain' }} />
                      )}

                      <Tooltip title="Remove Media" arrow placement="left">
                        <IconButton
                          size="small"
                          onClick={() => { setMediaPreview(''); setMediaUrl(''); setMediaFile(null); }}
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: 'rgba(239, 68, 68, 0.9)',
                            color: '#ffffff',
                            backdropFilter: 'blur(4px)',
                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            p: 1,
                            zIndex: 10,
                            '&:hover': {
                              bgcolor: '#dc2626',
                              transform: 'scale(1.15)',
                              boxShadow: '0 6px 16px rgba(220, 38, 38, 0.6)',
                            },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>


                      {uploadingMedia && (
                        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                          <Stack alignItems="center" spacing={1}>
                            <CircularProgress color="inherit" size={32} />
                            <Typography variant="caption">Uploading asset...</Typography>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box
                      component="label"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        minHeight: 180,
                        p: 3,
                        borderRadius: 3,
                        border: '2px dashed #cbd5e1',
                        bgcolor: '#f8fafc',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: '#f1f5f9',
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <input type="file" accept="image/*,video/*" hidden onChange={handleFileSelect} />
                      <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: '#F3E8FF', color: 'primary.main', mb: 1, display: 'flex' }}>
                        <CloudUpload sx={{ fontSize: 32 }} />
                      </Box>
                      <Typography fontWeight={700} variant="body1" color="text.primary" textAlign="center">
                        Click to upload Image or Video
                      </Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="center" mt={0.5}>
                        Supports JPEG, PNG, WEBP, MP4, MOV, WEBM (Max 100 MB)
                      </Typography>
                    </Box>
                  )}


                  {/* Or Manual URL Input */}
                  <Box mt={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Or enter public Media URL"
                      placeholder="https://example.com/photo.jpg"
                      value={mediaUrl}
                      onChange={(e) => {
                        setMediaUrl(e.target.value);
                        setMediaPreview(e.target.value);
                        if (e.target.value.match(/\.(mp4|mov|webm)(\?.*)?$/i)) {
                          setMediaType('video');
                        } else {
                          setMediaType('image');
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Target Platforms */}
                <Box mb={3}>
                  <FormLabel sx={{ fontWeight: 600, mb: 1, display: 'block' }}>Target Platforms</FormLabel>
                  <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                    {/* Facebook Page Button */}
                    <Button
                      onClick={() => handlePlatformToggle('facebook')}
                      startIcon={<Facebook sx={{ color: targetPlatforms.includes('facebook') ? '#ffffff !important' : '#1877F2 !important', fontSize: '20px !important' }} />}
                      endIcon={targetPlatforms.includes('facebook') ? <CheckCircle sx={{ fontSize: '16px !important', color: '#ffffff !important' }} /> : null}
                      sx={{
                        px: 2.5,
                        py: 1,
                        borderRadius: '24px',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        transition: 'all 0.2s ease-in-out',
                        ...(targetPlatforms.includes('facebook') ? {
                          bgcolor: '#1877F2',
                          color: '#ffffff',
                          border: '1.5px solid #1877F2',
                          boxShadow: '0 4px 14px rgba(24, 119, 242, 0.35)',
                          '&:hover': {
                            bgcolor: '#166fe5',
                            boxShadow: '0 6px 18px rgba(24, 119, 242, 0.45)',
                          },
                        } : {
                          bgcolor: '#f8fafc',
                          color: '#475569',
                          border: '1.5px solid #e2e8f0',
                          '&:hover': {
                            bgcolor: '#f1f5f9',
                            borderColor: '#cbd5e1',
                            color: '#1e293b',
                          },
                        }),
                      }}
                    >
                      Facebook Page
                    </Button>

                    {/* Instagram Business Button */}
                    <Button
                      onClick={() => handlePlatformToggle('instagram')}
                      startIcon={<Instagram sx={{ color: targetPlatforms.includes('instagram') ? '#ffffff !important' : '#dc2743 !important', fontSize: '20px !important' }} />}
                      endIcon={targetPlatforms.includes('instagram') ? <CheckCircle sx={{ fontSize: '16px !important', color: '#ffffff !important' }} /> : null}
                      sx={{
                        px: 2.5,
                        py: 1,
                        borderRadius: '24px',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        transition: 'all 0.2s ease-in-out',
                        ...(targetPlatforms.includes('instagram') ? {
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#ffffff',
                          border: 'none',
                          boxShadow: '0 4px 14px rgba(220, 39, 67, 0.35)',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #e08322 0%, #d5572b 25%, #cb1632 50%, #bb1255 75%, #ab0777 100%)',
                            boxShadow: '0 6px 18px rgba(220, 39, 67, 0.45)',
                          },
                        } : {
                          bgcolor: '#f8fafc',
                          color: '#475569',
                          border: '1.5px solid #e2e8f0',
                          '&:hover': {
                            bgcolor: '#f1f5f9',
                            borderColor: '#cbd5e1',
                            color: '#1e293b',
                          },
                        }),
                      }}
                    >
                      Instagram Business
                    </Button>
                  </Stack>
                </Box>



                {/* Post Type Selector */}
                <Box mb={3}>
                  <FormLabel sx={{ fontWeight: 600, mb: 1, display: 'block' }}>Post Format</FormLabel>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant={mediaType === 'image' ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={<AddPhotoAlternate />}
                      onClick={() => setMediaType('image')}
                    >
                      Photo Post
                    </Button>
                    <Button
                      variant={mediaType === 'video' ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={<Movie />}
                      onClick={() => setMediaType('video')}
                    >
                      Video / Reel
                    </Button>
                  </Stack>
                </Box>

                {/* Caption Input */}
                <Box mb={3}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <FormLabel sx={{ fontWeight: 600 }}>Caption / Post Text</FormLabel>
                    <Typography variant="caption" color="text.secondary">
                      {caption.length} characters
                    </Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Write a compelling caption for your gym members & followers..."
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                  />

                  {/* Hashtag & Emoji Shortcuts */}
                  <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" gap={0.5}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Tag fontSize="inherit" sx={{ mr: 0.5 }} /> Add Hashtags:
                    </Typography>
                    {popularHashtags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" onClick={() => handleAddHashtag(tag)} variant="outlined" sx={{ cursor: 'pointer' }} />
                    ))}
                  </Stack>

                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" gap={0.5}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <EmojiEmotions fontSize="inherit" sx={{ mr: 0.5 }} /> Add Emojis:
                    </Typography>
                    {popularEmojis.map((emoji) => (
                      <Button key={emoji} size="small" sx={{ minWidth: 32, p: 0.5, fontSize: '1.1rem' }} onClick={() => handleAddEmoji(emoji)}>
                        {emoji}
                      </Button>
                    ))}
                  </Stack>
                </Box>

                {/* Schedule Option */}
                <Box mb={3}>
                  <FormControlLabel
                    control={<Switch checked={isScheduled} onChange={(e) => setIsScheduled(e.target.checked)} />}
                    label={<Typography fontWeight={600}>Schedule for later</Typography>}
                  />

                  {isScheduled && (
                    <Box mt={2}>
                      <TextField
                        type="datetime-local"
                        label="Publish Date & Time"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={scheduledAt}
                        onChange={(e) => setScheduledAt(e.target.value)}
                      />
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Submit Action */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || uploadingMedia || (!mediaUrl && !mediaPreview) || targetPlatforms.length === 0}

                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : isScheduled ? <Schedule /> : <Share />}
                  onClick={handlePublish}
                  sx={{ py: 1.5, fontWeight: 700, fontSize: '1rem' }}
                >
                  {loading ? 'Publishing Post...' : isScheduled ? 'Schedule Social Post' : 'Publish Now to Social Media'}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Live Mobile Feed Preview */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ borderRadius: 3, position: 'sticky', top: 20 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" fontWeight={700}>
                    Live Mobile Feed Preview
                  </Typography>
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    icon={<CheckCircle sx={{ fontSize: '14px !important' }} />}
                    label="Live Preview Active"
                    sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Real-time preview of how your post will look on member mobile feeds.
                </Typography>

                <Tabs value={previewTab} onChange={(e, v) => setPreviewTab(v)} size="small" sx={{ mb: 2 }}>
                  <Tab icon={<Facebook sx={{ fontSize: 18, color: '#1877F2' }} />} iconPosition="start" label="Facebook" value="facebook" />
                  <Tab icon={<Instagram sx={{ fontSize: 18, color: '#E4405F' }} />} iconPosition="start" label="Instagram" value="instagram" />
                </Tabs>

                {/* Smartphone Preview Container */}
                <Box
                  sx={{
                    maxWidth: 360,
                    mx: 'auto',
                    border: '2px solid',
                    borderColor: previewTab === 'facebook' ? '#1877F2' : '#E4405F',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                    bgcolor: 'background.paper',
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  {/* Mock Mobile Header */}
                  <Stack direction="row" alignItems="center" spacing={1.5} p={1.5} borderBottom="1px solid" borderColor="divider" bgcolor="#f8fafc">
                    <Avatar
                      src={previewTab === 'facebook' ? (pageInfo?.page?.picture || '') : (pageInfo?.instagram?.picture || '')}
                      sx={{ width: 38, height: 38, border: '2px solid #fff', boxShadow: 1 }}
                    >
                      {previewTab === 'facebook' ? <Facebook /> : <Instagram />}
                    </Avatar>
                    <Box flexGrow={1} minWidth={0}>
                      <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2} noWrap>
                        {previewTab === 'facebook'
                          ? (pageInfo?.page?.name || localStorage.getItem('gymName') || 'Fitistic Gym')
                          : (pageInfo?.instagram?.username ? `@${pageInfo.instagram.username}` : 'fitistic_gym')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Just now • Sponsored / Official
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Media Content */}
                  <Box sx={{ bgcolor: '#090d16', minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    {mediaPreview ? (
                      mediaType === 'video' ? (
                        <video src={mediaPreview} controls autoPlay muted loop style={{ width: '100%', maxHeight: 320, objectFit: 'contain' }} />
                      ) : (
                        <img src={mediaPreview} alt="Mobile preview" style={{ width: '100%', maxHeight: 320, objectFit: 'cover' }} />
                      )
                    ) : (
                      <Stack alignItems="center" spacing={1.5} color="text.secondary" p={4} textAlign="center">
                        <Box sx={{ p: 2, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)', display: 'flex' }}>
                          <AddPhotoAlternate sx={{ fontSize: 36, color: '#94a3b8' }} />
                        </Box>
                        <Typography variant="body2" fontWeight={600} color="#e2e8f0">
                          Upload media or enter URL to render live preview
                        </Typography>
                        <Typography variant="caption" color="#94a3b8">
                          Preview automatically syncs with selected platforms
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  {/* Caption Content */}
                  <Box p={2}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: caption ? 'text.primary' : 'text.secondary', fontStyle: caption ? 'normal' : 'italic' }}>
                      {caption || 'Your post caption and hashtags will appear live here...'}
                    </Typography>

                    {/* Character count & Hashtag chips preview */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1.5}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {caption.length} characters
                      </Typography>
                      <Chip label={previewTab === 'facebook' ? 'Feed Post' : 'Reel / Post'} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                    </Stack>

                    {/* Mock Social Interactions */}
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" justifyContent="space-around" color="text.secondary" fontSize="0.75rem">
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'pointer', '&:hover': { color: '#1877F2' } }}>
                        <ThumbUp sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={600}>Like</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'pointer', '&:hover': { color: '#10b981' } }}>
                        <ChatBubbleOutline sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={600}>Comment</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'pointer', '&:hover': { color: '#8b5cf6' } }}>
                        <Share sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={600}>Share</Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 1: POSTS HISTORY & GALLERY ────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          <Typography variant="h6" fontWeight={700} mb={2}>
            Published & Scheduled Social Posts
          </Typography>

          {postsLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (() => {
            const validPosts = posts.filter((p) => p.status !== 'failed');
            return validPosts.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                <PhotoLibrary sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6">No social media posts yet</Typography>
                <Typography color="text.secondary" mb={2}>
                  Create your first image or video post to start building your gym brand on Facebook and Instagram!
                </Typography>
                <Button variant="contained" onClick={() => setTab(0)}>
                  Create Post Now
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {validPosts.map((post) => {
                  const fbUrl = post.facebookUrl || (post.facebookPostId ? (post.facebookPostId.includes('_') ? `https://www.facebook.com/${post.facebookPostId.split('_')[0]}/posts/${post.facebookPostId.split('_')[1]}` : `https://facebook.com/${post.facebookPostId}`) : null);
                  const igUrl = post.instagramUrl || (post.instagramMediaId ? `https://instagram.com/p/${post.instagramMediaId}` : null);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={post._id}>
                      <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
                        {/* Media Thumbnail */}
                        <Box sx={{ position: 'relative', bgcolor: 'black', height: 200, overflow: 'hidden' }}>
                          {post.mediaType === 'video' ? (
                            <video src={post.mediaUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <img src={post.mediaUrl} alt="Post asset" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          )}

                          {/* Status Badge */}
                          <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
                            {post.status === 'published' && <Chip size="small" label="Published" color="success" icon={<CheckCircle sx={{ fontSize: '14px !important' }} />} />}
                            {post.status === 'scheduled' && <Chip size="small" label="Scheduled" color="info" icon={<Schedule sx={{ fontSize: '14px !important' }} />} />}
                          </Box>

                          {/* Media type icon */}
                          {post.mediaType === 'video' && (
                            <Box sx={{ position: 'absolute', bottom: 12, right: 12, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', p: 0.5, borderRadius: 1, display: 'flex' }}>
                              <Movie fontSize="small" />
                            </Box>
                          )}
                        </Box>

                        {/* Post Content */}
                        <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', mb: 1.5, flexGrow: 1 }}>
                            {post.caption || 'No caption provided.'}
                          </Typography>

                          {/* Target Platform Badges & Direct Links */}
                          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={0.5}>
                            {post.targetPlatforms?.includes('facebook') && (
                              <Chip
                                size="small"
                                icon={<Facebook sx={{ fontSize: '14px !important' }} />}
                                label="Facebook"
                                component={fbUrl ? 'a' : 'div'}
                                href={fbUrl || undefined}
                                target="_blank"
                                clickable={!!fbUrl}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            {post.targetPlatforms?.includes('instagram') && (
                              <Chip
                                size="small"
                                icon={<Instagram sx={{ fontSize: '14px !important' }} />}
                                label="Instagram"
                                component={igUrl ? 'a' : 'div'}
                                href={igUrl || undefined}
                                target="_blank"
                                clickable={!!igUrl}
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                          </Stack>

                          {/* Metrics & Insights Bar */}
                          <Paper variant="outlined" sx={{ p: 1.2, mb: 1.5, borderRadius: 2, bgcolor: '#f8fafc', borderColor: '#e2e8f0' }}>
                            <Stack direction="row" justifyContent="space-around" alignItems="center">
                              <Tooltip title="Likes">
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <ThumbUp sx={{ fontSize: 16, color: '#1877F2' }} />
                                  <Typography variant="caption" fontWeight={700}>
                                    {post.likesCount || 0}
                                  </Typography>
                                </Stack>
                              </Tooltip>

                              <Tooltip title="Comments">
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <ChatBubbleOutline sx={{ fontSize: 16, color: '#0284c7' }} />
                                  <Typography variant="caption" fontWeight={700}>
                                    {post.commentsCount || 0}
                                  </Typography>
                                </Stack>
                              </Tooltip>

                              <Tooltip title="Impressions / Views">
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <BarChart sx={{ fontSize: 16, color: '#16a34a' }} />
                                  <Typography variant="caption" fontWeight={700}>
                                    {post.viewsCount || post.reachCount || 0}
                                  </Typography>
                                </Stack>
                              </Tooltip>
                            </Stack>
                          </Paper>

                          <Divider sx={{ my: 1 }} />

                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              {new Date(post.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </Typography>

                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Sync Live Likes & Comments">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={syncingPostId === post._id}
                                  onClick={() => handleSyncSinglePost(post._id)}
                                >
                                  {syncingPostId === post._id ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <Refresh fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>

                              {fbUrl && (
                                <Tooltip title="Open Facebook Post">
                                  <IconButton size="small" component="a" href={fbUrl} target="_blank">
                                    <OpenInNew color="primary" fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {igUrl && (
                                <Tooltip title="Open Instagram Post">
                                  <IconButton size="small" component="a" href={igUrl} target="_blank">
                                    <OpenInNew color="secondary" fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Delete Post Record">
                                <IconButton size="small" color="error" onClick={() => setDeleteId(post._id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>

                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            );
          })()}

        </Box>
      )}

      {/* ── TAB 2: META CAMPAIGNS & LEAD FORMS ────────────────────────────── */}
      {tab === 2 && (
        <MetaAdsManager showContentOnly={true} />
      )}

      {/* Lock Overlay when No Meta Account is Connected */}
      {!metaAccountLoading && !metaAccount && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(248, 250, 252, 0.84)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 8,
            borderRadius: 3,
            px: 2,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 4,
              maxWidth: 520,
              borderRadius: 4,
              textAlign: 'center',
              border: '1px solid #e2e8f0',
              background: '#ffffff',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#eff6ff',
                color: '#1877F2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 4px 12px rgba(24, 119, 242, 0.2)',
              }}
            >
              <Lock sx={{ fontSize: 36 }} />
            </Box>

            <Typography variant="h5" fontWeight={800} color="text.primary" gutterBottom>
              Connect Meta Account to Unlock
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              You must connect your business Facebook Page & Instagram Account before accessing post creation, post history, or lead campaigns.
            </Typography>

            <Button
              variant="contained"
              size="large"
              startIcon={<Facebook />}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{
                bgcolor: '#1877F2',
                '&:hover': { bgcolor: '#166fe5' },
                fontWeight: 700,
                px: 4,
                py: 1.2,
                borderRadius: 3,
                boxShadow: '0 4px 14px rgba(24, 119, 242, 0.35)',
              }}
            >
              Connect Meta Account Above
            </Button>
          </Paper>
        </Box>
      )}
    </Box>




      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Social Post Record?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this social post record from your dashboard? (Note: Published posts on Facebook or Instagram will not be removed from the social platforms).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeletePost}>
            Delete Record
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}
